import React, { ReactElement, ReactNode } from 'react';
import classNames from 'classnames';
import Box from '../Box';
import Text from '../Text';
import { DecorativeIconProvider } from '../IconBase';

import { getResponsiveValue, ResponsiveValue } from '../_internal/styling';
import useScreenSize from '../_internal/useScreenSize';
import useUniqueId from '../_internal/useUniqueId';
import logger from '../_internal/logger';
import {
  getCircularArcPath,
  getReloadArrowPath,
  getProcessCheckPathInfo,
} from './utils';
import IconHangerLarge from './IconHangerLarge';
import IconHangerXLarge from './IconHangerXLarge';

import styles from './spinner.module.scss';

type Size = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

const SIZE_MAP: Record<
  Size,
  {
    arrow: {
      length: number;
      strokeWidth: number;
      rotation: number;
    };
    circle: {
      diameter: number;
      strokeWidth: number;
    };
    indeterminateDefaultIcon?: ReactElement;
  }
> = {
  xsmall: {
    circle: { diameter: 16, strokeWidth: 2 },
    arrow: { length: 4, strokeWidth: 1.5, rotation: 65 },
  },
  small: {
    circle: { diameter: 24, strokeWidth: 2 },
    arrow: { length: 4, strokeWidth: 2, rotation: 57.5 },
  },
  medium: {
    circle: { diameter: 32, strokeWidth: 3 },
    arrow: { length: 6, strokeWidth: 2.5, rotation: 57.5 },
  },
  large: {
    circle: {
      diameter: 56,
      strokeWidth: 3,
    },
    indeterminateDefaultIcon: (
      <IconHangerLarge
        purpose="decorative"
        data-testid="spinner-default-icon-large"
      />
    ),
    arrow: { length: 8, strokeWidth: 2.5, rotation: 55 },
  },
  xlarge: {
    circle: {
      diameter: 80,
      strokeWidth: 4,
    },
    indeterminateDefaultIcon: (
      <IconHangerXLarge
        purpose="decorative"
        data-testid="spinner-default-icon-xlarge"
      />
    ),
    arrow: { length: 10, strokeWidth: 3, rotation: 55 },
  },
};

interface ProcessVariantProps {
  /**
   * Whether or not the process spinner is done
   */
  done?: boolean;

  /**
   * Variant/style of the spinner
   */
  variant: 'process';
}

interface NonProcessVariantProps {
  variant?: 'indeterminate' | 'reload';
  done?: undefined;
}

interface CustomIconWithAriaProps {
  /**
   * An accessibility message describing what is loading. Required when
   * specifying a custom `icon`, but mutually exclusive with `children`.
   */
  'aria-label': string;
  /**
   * Text to include with the spinner. Required when specifying a custom `icon`, but mutually exclusive with `aria-label`.
   */
  children?: undefined;
  /**
   * An icon displayed in the middle of the spinner. The icon overrides the
   * default icon shown in `indeterminate` spinners. The icon is **only** shown
   * for `large` & `xlarge` sizes, and never included in `reload` spinners.
   */
  icon: ResponsiveValue<ReactElement>;
}

interface CustomIconWithChildrenProps {
  'aria-label'?: undefined;
  children: ReactNode;
  icon: ResponsiveValue<ReactElement>;
}

interface NoCustomIconWithOptionalAriaProps {
  'aria-label'?: string;
  children?: undefined;
  icon?: undefined;
}

interface NoCustomIconWithOptionalChildrenProps {
  'aria-label'?: undefined;
  children?: ReactNode;
  icon?: undefined;
}

// An `aria-label` or `children` is *required* if the custom `icon` is
// specified, and optional if not specified. However, we cannot specify both
// because they would be redundant.
type IconProps =
  | CustomIconWithAriaProps
  | CustomIconWithChildrenProps
  | NoCustomIconWithOptionalAriaProps
  | NoCustomIconWithOptionalChildrenProps;

interface CommonProps {
  /**
   * The unique ID of the element on the page that is loading/busy
   */
  'aria-describedby'?: string;
  /**
   * Color of the spinner
   */
  color?: ResponsiveValue<'default' | 'accent' | 'inverse'>;
  /**
   * Size of the spinner
   *
   * @default 'large'
   */
  size?: ResponsiveValue<Size>;
}

export type SpinnerProps = CommonProps &
  IconProps &
  (ProcessVariantProps | NonProcessVariantProps);

/**
 * Spinners should be briefly shown when we can't clearly or accurately indicate progress of a system process. Since they have an unknown end, they are more effective when they are shown for a very short amount of time. However, they also should have a minimum time they're on screen because users are wary of instant or jumpy UI changes.
 */
const Spinner = ({
  'aria-describedby': ariaDescribedBy,
  'aria-label': customAriaLabel,
  children,
  color = 'default',
  done = false,
  icon: customIcon,
  size = 'large',
  variant = 'indeterminate',
}: SpinnerProps) => {
  const spinnerId = useUniqueId('spinner');
  const screenSize = useScreenSize();

  const respSize = getResponsiveValue(screenSize, size, 'large');
  const respColor = getResponsiveValue(screenSize, color, 'default');
  const respCustomIcon = getResponsiveValue(screenSize, customIcon);

  const {
    circle: { diameter, strokeWidth },
    indeterminateDefaultIcon,
    arrow: {
      length: arrowLength,
      strokeWidth: arrowStrokeWidth,
      rotation: arrowRotation,
    },
  } = SIZE_MAP[respSize];

  // the default icon is only used in "indeterminate" spinners. "process"
  // spinners only support custom icons.
  const icon =
    respCustomIcon ??
    (variant === 'indeterminate' ? indeterminateDefaultIcon : undefined);

  // All the sizes that have default icons are the sizes that allow icons
  const sizeAllowsIcon = !!indeterminateDefaultIcon;

  const variantAllowsIcon = variant !== 'reload';
  const canDisplayIcon = !!icon && sizeAllowsIcon && variantAllowsIcon;
  const hasArrow = variant === 'reload';
  const displayArrowLength = hasArrow ? arrowLength : 0;

  // TypeScript is already verifying that `aria-label` is passed when a
  // custom icon is specified, but for those not using TypeScript we want to
  // ensure this accessibility requirement is handled.
  if (respCustomIcon && canDisplayIcon && !customAriaLabel && !children) {
    logger.warn(
      'Spinner must include `aria-label` or `children` when specifying a custom `icon` to satisfy accessibility needs.',
    );
  }

  // We should only be displaying either aria-labelledby or aria-label, but not
  // both. So if both the `aria-label` and `children` are specified, then
  // `children` wins and we'll only use `aria-labelledby`. TypeScript handles
  // this but for non-TS users we want to ensure we don't specify both. In
  // addition, one of them is *still* required by `progressbar` even if there
  // isn't a custom icon. So in that case we default the aria label so we've got
  // something.
  const textId = `${spinnerId}-text`;
  const ariaLabelledBy = children ? textId : undefined;
  const defaultAriaLabel = !children ? 'Loading' : undefined;
  const ariaLabel = !ariaLabelledBy
    ? customAriaLabel ?? defaultAriaLabel
    : undefined;

  return (
    <Box
      className={classNames(styles.root, styles[respSize], styles[respColor], {
        [styles.process]: variant === 'process',
        [styles.done]: done,
      })}
      role="progressbar"
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      <Box className={styles['svg-container']}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={diameter}
          height={diameter}
          viewBox={`0 0 ${diameter} ${diameter}`}
          fill="none"
        >
          <defs>
            <linearGradient id={`${spinnerId}-secondHalf`}>
              <stop offset="0%" stopOpacity="0" stopColor="currentColor" />
              <stop offset="100%" stopOpacity="0.5" stopColor="currentColor" />
            </linearGradient>

            <linearGradient id={`${spinnerId}-firstHalf`}>
              <stop offset="0%" stopOpacity="1" stopColor="currentColor" />
              <stop offset="100%" stopOpacity="0.5" stopColor="currentColor" />
            </linearGradient>
          </defs>

          <g className={styles['rotation-group']}>
            <g strokeWidth={strokeWidth}>
              <path
                // second half of the gradient circle from 0% to 50% opacity
                d={getCircularArcPath({
                  diameter,
                  strokeWidth,
                  startAngle: 180,
                  sweep: 180,
                  arrowLength: displayArrowLength,
                  arrowRotation,
                })}
                stroke={`url(#${spinnerId}-secondHalf)`}
              />
              <path
                // first half of the gradient circle from 100% to 50% opacity
                d={getCircularArcPath({
                  diameter,
                  strokeWidth,
                  startAngle: 0,
                  sweep: 180,
                  arrowLength: displayArrowLength,
                  arrowRotation,
                })}
                stroke={`url(#${spinnerId}-firstHalf)`}
              />

              {!hasArrow && (
                <path
                  // 1deg extra path to have the round end cap at the start only
                  d={getCircularArcPath({
                    diameter,
                    strokeWidth,
                    startAngle: 180,
                    sweep: -1,
                  })}
                  stroke="currentColor"
                  strokeLinecap="round"
                />
              )}

              {variant === 'process' && (
                <path
                  // the circular path to fill with the solid color when the process
                  // loading spinner is done
                  className={styles['process-fill-path']}
                  d={getCircularArcPath({
                    diameter,
                    strokeWidth,
                    startAngle: 180,
                    sweep: 359.9,
                  })}
                  stroke="currentColor"
                  strokeLinecap="round"
                  // `stroke-dasharray` & `stroke-dashoffset` are used to animate
                  // drawing the path so we went them to the length of the circle
                  // (i.e. the circumference)
                  strokeDasharray={Math.PI * diameter}
                  strokeDashoffset={Math.PI * diameter}
                />
              )}
            </g>

            {hasArrow && (
              <path
                // the arrow head which has a different stroke width
                d={getReloadArrowPath({
                  diameter,
                  strokeWidth: arrowStrokeWidth,
                  length: displayArrowLength,
                  rotation: arrowRotation,
                })}
                stroke="currentColor"
                strokeWidth={arrowStrokeWidth}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            )}
          </g>

          {variant === 'process' && (
            // separate, non-spinning portion of the SVG for the animating check
            // mark of the process loading spinner
            <path
              className={styles['process-checkmark-path']}
              stroke="currentColor"
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              {...getProcessCheckPathInfo(diameter)}
            />
          )}
        </svg>

        {canDisplayIcon && (
          <DecorativeIconProvider defaultColor="inherit">
            <Box className={styles['icon-container']}>{icon}</Box>
          </DecorativeIconProvider>
        )}
      </Box>

      {children && (
        <Text id={textId} className={styles.text} setting="body-medium">
          {children}
        </Text>
      )}
    </Box>
  );
};

export default Spinner;
